import { useState, useEffect } from "react";
import { Link } from "wouter";
import { Menu, UserPlus, BookOpen, LogIn, LogOut } from "lucide-react";
import { useAppAuth } from "@/components/AuthProvider";
import { SignedIn, SignedOut, UserButton, SignInButton } from "@clerk/clerk-react";

export default function Navbar() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { user, isSignedIn, logout, isLoading } = useAppAuth();

  // Get user subscription status for display
  const getUserStatusLabel = () => {
    if (isLoading || !isSignedIn || !user || !user.subscriptionStatus) return null;
    
    const statusColors = {
      'mentorship': 'bg-purple-100 text-purple-800 border-purple-300',
      'active': 'bg-green-100 text-green-800 border-green-300',
      'inactive': 'bg-gray-100 text-gray-800 border-gray-300'
    };
    
    // Ensure user.subscriptionStatus is one of the keys, or provide a default
    const currentStatus = user.subscriptionStatus as keyof typeof statusColors;
    if (!statusColors[currentStatus]) return null; // Or a default badge

    return (
      <div className={`text-xs border px-2 py-0.5 rounded-full ${statusColors[currentStatus]}`}>
        {user.subscriptionStatus === 'mentorship' ? 'Mentorship' : 
         user.subscriptionStatus === 'active' ? 'Premium' : 'Basic'}
      </div>
    );
  };

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 10) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <nav className={`sticky top-0 z-50 bg-white border-b border-gray-100 ${scrolled ? 'shadow-sm' : ''} transition-shadow duration-300`}>
      <div className="container mx-auto px-4 py-3 flex justify-between items-center max-w-5xl">
        <Link href="/" className="text-xl font-serif font-medium text-indigo-950">
          Next Generation Medicine
        </Link>
        
        <div className="hidden md:flex items-center space-x-8">
          <Link href="/consulting" className="text-sm text-gray-700 hover:text-indigo-900 transition-colors">
            Consulting
          </Link>
          <Link href="/mentorship" className="text-sm text-gray-700 hover:text-indigo-900 transition-colors">
            Mentorship
          </Link>
          <Link href="/patients" className="text-sm text-gray-700 hover:text-indigo-900 transition-colors">
            Patients
          </Link>
          
          <SignedIn>
            <Link href="/mentorship-content" className="flex items-center text-sm text-gray-700 hover:text-indigo-900 transition-colors">
              <BookOpen className="h-4 w-4 mr-1" />
              Mentorship Content
            </Link>
          </SignedIn>
          
          <div className="flex items-center gap-3">
            <SignedIn>
              <>
                {getUserStatusLabel()}
                <UserButton afterSignOutUrl="/" />
              </>
            </SignedIn>
            <SignedOut>
              <SignInButton mode="modal" redirectUrl="/">
                <button className="flex items-center px-3 py-1 bg-indigo-950 text-white text-xs rounded-sm hover:bg-indigo-900 transition-colors">
                  <LogIn className="h-3 w-3 mr-1" />
                  Login
                </button>
              </SignInButton>
            </SignedOut>
          </div>
        </div>
        
        <button 
          className="md:hidden text-indigo-950" 
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="Toggle mobile menu"
        >
          <Menu className="h-5 w-5" />
        </button>
      </div>
      
      {mobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-gray-100">
          <div className="flex flex-col px-4 py-2 space-y-3">
            <Link 
              href="/consulting" 
              className="py-2 text-sm text-gray-700 hover:text-indigo-900"
              onClick={() => setMobileMenuOpen(false)}
            >
              Consulting
            </Link>
            <Link 
              href="/mentorship" 
              className="py-2 text-sm text-gray-700 hover:text-indigo-900"
              onClick={() => setMobileMenuOpen(false)}
            >
              Mentorship
            </Link>
            <Link 
              href="/patients" 
              className="py-2 text-sm text-gray-700 hover:text-indigo-900"
              onClick={() => setMobileMenuOpen(false)}
            >
              Patients
            </Link>
            
            <SignedIn>
              <Link 
                href="/mentorship-content" 
                className="py-2 flex items-center text-sm text-gray-700 hover:text-indigo-900"
                onClick={() => setMobileMenuOpen(false)}
              >
                <BookOpen className="h-4 w-4 mr-1" />
                Mentorship Content
              </Link>
            </SignedIn>
            
            <SignedIn>
              <>
                <div className="py-2">
                  {getUserStatusLabel()}
                </div>
                <button 
                  onClick={() => {
                    logout();
                    setMobileMenuOpen(false);
                  }}
                  className="py-2 flex items-center text-xs text-red-700 font-medium"
                >
                  <LogOut className="h-3 w-3 mr-1" />
                  Logout
                </button>
              </>
            </SignedIn>
            <SignedOut>
              <SignInButton mode="modal" redirectUrl="/">
                <button 
                  onClick={() => setMobileMenuOpen(false)} 
                  className="py-2 flex items-center text-xs text-indigo-950 font-medium"
                >
                  <LogIn className="h-3 w-3 mr-1" />
                  Login
                </button>
              </SignInButton>
            </SignedOut>
          </div>
        </div>
      )}
    </nav>
  );
}
